import clsx from 'clsx'
import renderSelect from 'components/FormControls/select'
import GridSpinner from 'components/UI/GridSpinner'
import { Button, Icon, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Field } from 'redux-form'
import styles from 'modules/Forms/SharedControls/PaymentProfilesControl/PaymentProfilesControl.module.scss'
import { createPath } from 'history'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import qs from 'qs'

const PaymentProfilesControl = ({
  className = '',
  loading = false,
  disabled = false,
  paymentProfiles = null,
  t = () => {},
  readOnly = false,
  addUrlParams = {},
}) => {
  const pathGen = useGeneratePathI18n()

  const paymentProfilesOptions = useMemo(
    () =>
      !paymentProfiles || paymentProfiles.length === 0
        ? []
        : paymentProfiles.map((profile) => ({
            ...profile,
            disabled: profile.status === 'declined',
            value: profile.id,
          })),
    [paymentProfiles],
  )

  if (!paymentProfiles) {
    return null
  }

  return (
    <div className={className}>
      <Field
        disabled={disabled}
        readOnly={readOnly}
        component={renderSelect}
        name={'payment_profile_id'}
        fullWidth
        t={t}
        showEmptyOption={!readOnly}
        emptyOptionLabel={t(`services::forms.shared_fields.payment_profile_id.options.empty`)}
        clearOptionLabel={t('common::select_clear_option')}
        helperText={
          !loading && paymentProfiles.length === 0 ? (
            <>
              <span
                dangerouslySetInnerHTML={{
                  __html: t(`services::forms.shared_fields.payment_profile_id.helperText.empty`),
                }}
              />{' '}
              <Button
                variant={'primary'}
                link
                size={'s'}
                component={Link}
                disabled={disabled}
                to={createPath({
                  pathname: pathGen(ROUTES.FINANCE_PAYMENT_PROFILE_NEW),
                  search: qs.stringify(
                    {
                      ...addUrlParams,
                      goBack: global.location.pathname + global.location.search,
                    },
                    {
                      arrayFormat: 'brackets',
                    },
                  ),
                })}
              >
                <Typography variant={'Body-10'}>
                  {t(
                    `services::forms.shared_fields.payment_profile_id.helperText.empty_create_label`,
                  )}
                </Typography>
              </Button>
            </>
          ) : null
        }
        label={t(`services::forms.shared_fields.payment_profile_id.label`)}
        activeOptionLabelTemplate={(item) => {
          const tax_number = item?.tax_info?.tax_number ? ' — ' + item.tax_info.tax_number : ''
          return `${item.title}${tax_number}`
        }}
        optionLabelTemplate={({ status, tax_info = {}, decline_reason = null, title = '' }) => (
          <div
            className={clsx({
              [styles['payment-profile__declined']]: status === 'declined',
            })}
          >
            <Typography
              variant={'Body-25'}
              noMargin
              component={'div'}
            >
              {title}
            </Typography>
            <Typography
              variant={'Body-10'}
              noMargin
              component={'div'}
            >
              {tax_info?.tax_number}
            </Typography>
            {status === 'declined' && (
              <div className={styles['payment-profile__error']}>
                {t('services::forms.shared_fields.payment_profile_id.options.error_description')}{' '}
                {decline_reason ? `: ${decline_reason}` : ''}
              </div>
            )}
          </div>
        )}
        options={paymentProfilesOptions}
        ControlProps={{
          startAdornment: loading && <GridSpinner width={24} />,
          endAdornment: (
            <Button
              component={Link}
              variant={'primary'}
              disabled={disabled}
              link
              to={createPath({
                pathname: pathGen(ROUTES.FINANCE_PAYMENT_PROFILE_NEW),
                search: qs.stringify(
                  {
                    ...addUrlParams,
                    goBack: global.location.pathname + global.location.search,
                  },
                  {
                    arrayFormat: 'brackets',
                  },
                ),
              })}
              className={styles['payment-profiles-field__link']}
              startIcon={
                <Icon
                  name={'plus'}
                  size={'s'}
                  color={'Interface-GU-Primary'}
                  className={styles['filter-button__icon']}
                />
              }
            >
              {t(
                `common::button_labels.${
                  !loading && paymentProfiles.length === 0 ? 'create' : 'add'
                }`,
              )}
            </Button>
          ),
        }}
      />
    </div>
  )
}
export default PaymentProfilesControl

PaymentProfilesControl.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  loading: PropTypes.bool,
  paymentProfiles: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  t: PropTypes.func,
  addUrlParams: PropTypes.object,
}
